import type { TypeId } from '@unipile/db';
import type {
  ContractSelectionCheckpoint,
  ProviderAuthenticationCheckpointInput,
  ProviderAuthenticationIntentResponse,
} from '@unipile/types';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSolveCheckpoint } from '../../../features/accounts/api/solve-checkpoint';
import { useProvider } from '../../../hooks/useProvider';
import { Button } from '../../shadcn/Button';
import { Field, FieldGroup } from '../../shadcn/Field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../shadcn/Select';
import { Spinner } from '../../shadcn/Spinner';
import { WizardStepContainer } from '../containers/WizardStepContainer';

/**
 * A checkpoint that requires a contract to be selected.
 */
export function ContractSelectionCheckpoint({
  checkpoint,
  onSuccess,
  onError,
  intent_id,
}: {
  onSuccess: (response: ProviderAuthenticationIntentResponse) => void;
  onError: (error: unknown) => void;
  checkpoint: ContractSelectionCheckpoint;
  intent_id: TypeId<'account'>;
}) {
  const form = useForm<ProviderAuthenticationCheckpointInput>();

  const { t } = useTranslation();
  const { provider } = useProvider();

  const { mutate, isPending } = useSolveCheckpoint({
    mutationConfig: {
      onSuccess,
      onError,
    },
  });

  const onSubmit = (data: ProviderAuthenticationCheckpointInput) => {
    mutate({ data: { ...data, intent_id } });
  };

  return (
    <WizardStepContainer
      title={t('CONTRACT_SELECTION.title', { ns: provider })}
      description={t('CONTRACT_SELECTION.description', { ns: provider })}
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup>
          <Controller
            control={form.control}
            name="code"
            rules={{ required: true }}
            render={({ field }) => (
              <Field>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('ContractSelectionCheckpoint.placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {checkpoint.contracts.map(({ id, name }) => (
                      <SelectItem key={id} value={id}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
          />

          <Button className="mt-8 w-full" type="submit" disabled={isPending}>
            {isPending && <Spinner />}
            {t('Common.next')}
          </Button>
        </FieldGroup>
      </form>
    </WizardStepContainer>
  );
}
